import { Soundfont, useAudioEngine, useInstrument } from '@tunasong/audio-ui'
import type { MelodyLib } from '@tunasong/music-lib'
import type { MelodyInstrumentHint } from '@tunasong/schemas'
import { useMemo, useRef, useState } from 'react'
import { melodyInstrument } from '../melody-roll/instrument-hint.js'

/**
 * Play a take that is still in milliseconds, and say where playback has got to on the take's own
 * clock.
 *
 * This is how §11.2 step 2 gets its taps. The alternative - playing the recorded blob back and
 * reading `currentTime`, the way "Extract melody" does - cannot be used here: the blob's t=0 is when
 * the recorder started, which is inside the lead-in and a different origin from the one the raw
 * onsets carry, and the constant difference between the two clocks would be baked straight into the
 * fitted phase and from there into every `offsetMs` in the melody. Playing the raw notes puts the
 * taps on exactly the timeline `fitBeatGrid` is about to fit.
 *
 * `takeTimeMs` is a function and not state: a tap is a point in time and must not be rounded to the
 * nearest render.
 *
 * There is no unit test for this file. Everything in it is either the audio engine's or arithmetic
 * that is asserted through the taps it produces, in `free-tempo-tap.test.tsx`.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §11.2
 */

export interface RawTakePlayback {
  play: () => void
  stop: () => void
  isPlaying: boolean
  /** Position on the RAW TAKE's clock right now, in ms, or null when nothing is playing. */
  takeTimeMs: () => number | null
}

export const useRawTakePlayback = (
  raw: MelodyLib.RawNote[],
  instrumentHint?: MelodyInstrumentHint
): RawTakePlayback => {
  const engine = useAudioEngine()
  const instrumentName = melodyInstrument(instrumentHint)

  /** One Soundfont per instrument name, not per render - the shape `useMelodyRollPlayback` uses. */
  const instrument = useMemo(
    () => new Soundfont(engine.mixer.context, instrumentName),
    [engine.mixer.context, instrumentName]
  )

  /**
   * Route to THIS hook's instrument: `playScheduled` with no id resolves to whichever instrument
   * mounted first on the page, and the failure is silent.
   */
  const instrumentId = useInstrument('melody-take', instrument) ?? undefined

  const [isPlaying, setIsPlaying] = useState(false)
  /** Wall clock at `play()`, and the take-clock value it corresponds to. */
  const startedAt = useRef<number | null>(null)
  const originMs = useRef(0)
  const endHandle = useRef<number | null>(null)

  const stop = () => {
    if (endHandle.current !== null) {
      window.clearTimeout(endHandle.current)
      endHandle.current = null
    }
    startedAt.current = null
    setIsPlaying(false)
  }

  const play = () => {
    stop()
    if (raw.length === 0) {
      return
    }

    /** Playback starts at the first note, so the user is not tapping through the lead-in's silence. */
    const origin = raw[0].onsetMs
    originMs.current = origin

    engine.playScheduled(
      raw.map(note => ({
        midi: note.midi,
        velocity: note.velocity,
        timeMs: note.onsetMs - origin,
        durationMs: note.durationMs,
      })),
      instrumentId
    )

    const last = raw[raw.length - 1]
    startedAt.current = performance.now()
    setIsPlaying(true)
    endHandle.current = window.setTimeout(stop, last.onsetMs - origin + last.durationMs)
  }

  const takeTimeMs = () =>
    startedAt.current === null ? null : performance.now() - startedAt.current + originMs.current

  return { play, stop, isPlaying, takeTimeMs }
}
